import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Input } from "@heroui/react";
import { productsIndex } from "src/services";
import {
	getCartCost,
	TOrder,
	TProduct,
	TCartItemProduct,
	TFulfillmentStatus,
	TOrganization,
} from "@jsdev_ninja/core";
import { Button } from "src/components/button";
import { formatter } from "src/utils/formatter";
import { modalApi } from "src/infra/modals";
import { useAppApi } from "src/appApi";
import { useDiscounts } from "src/domains/Discounts/Discounts";
import { useStore } from "src/domains/Store";
import { QtyInput } from "./QtyInput";
import { WEIGHT_STEP, clampQty, isWeightProduct } from "./qtyUtils";

const unit = (it: TCartItemProduct) => it.finalPrice || it.originalPrice || it.product.price || 0;
const nameOf = (it: TCartItemProduct) => it.product.name?.[0]?.value || "—";

function isWeightItem(it: TCartItemProduct): boolean {
	return isWeightProduct(it.product);
}

/**
 * Collapse repeated plain lines of the same product (same product id + unit
 * price, no missing/substituted status) into one line with the summed amount.
 * Orders can end up with the same product on several lines (e.g. added again
 * via "add item"); showing them merged lets the admin edit one real quantity.
 */
function mergeDuplicateLines(items: TCartItemProduct[]): TCartItemProduct[] {
	const isPlain = (it: TCartItemProduct) => !it.status || it.status === "delivered";
	const out: TCartItemProduct[] = [];
	for (const it of items) {
		const match = isPlain(it)
			? out.find((o) => o.product.id === it.product.id && isPlain(o) && unit(o) === unit(it))
			: undefined;
		if (match) match.amount = clampQty(match.amount + it.amount, isWeightItem(match));
		else out.push({ ...it });
	}
	return out;
}

function lineTotal(it: TCartItemProduct): number {
	if (it.status === "missing") return 0;
	if (it.status === "substituted" && it.substitutedWith)
		return it.substitutedWith.price * it.substitutedWith.amount;
	return unit(it) * it.amount;
}

/**
 * Edit-order modal (demo-style). Admin composition editing before approval:
 * qty steppers, add / remove items, per-line fulfillment status + substitution,
 * billing account, and notes. Saves items + fulfilled cart.cartTotal via updateOrder.
 */
export function OrderEditModal({
	order,
	onSaved,
}: {
	order: TOrder;
	onSaved?: (order: TOrder) => void;
}) {
	const { t } = useTranslation(["common", "ordersPage"]);
	const appApi = useAppApi();
	const discounts = useDiscounts();
	const store = useStore();

	const [items, setItems] = useState<TCartItemProduct[]>(() =>
		mergeDuplicateLines(order.cart.items ?? []),
	);
	const [notes, setNotes] = useState(order.clientComment ?? "");
	const [accountId, setAccountId] = useState(order.billingAccount?.id ?? "");
	const [orgs, setOrgs] = useState<TOrganization[]>([]);
	const [saving, setSaving] = useState(false);
	const [freeShipping, setFreeShipping] = useState(order.storeOptions?.freeShipping ?? false);

	// Algolia search: add-item box + per-line substitution box. Inputs are
	// CONTROLLED (query state) so re-renders from results don't drop the text.
	const [addQuery, setAddQuery] = useState("");
	const [addResults, setAddResults] = useState<TProduct[]>([]);
	const [addOpen, setAddOpen] = useState(false);
	// Product picked from the add-item search, held until the admin confirms the
	// quantity — the line is only appended when "add" is pressed.
	const [addPending, setAddPending] = useState<TProduct | null>(null);
	const [addQty, setAddQty] = useState(1);
	const [subFor, setSubFor] = useState<number | null>(null);
	const [subQuery, setSubQuery] = useState("");
	const [subResults, setSubResults] = useState<TProduct[]>([]);

	useEffect(() => {
		appApi.admin.listOrganizations().then((res) => {
			if (res?.success) setOrgs(res.data ?? []);
		});
	}, []);

	const org = orgs.find((o) => o.id === order.organizationId);
	const accounts = org?.billingAccounts ?? [];
	const close = () => modalApi.closeModal("orderEdit");

	async function search(query: string, target: "add" | number) {
		target === "add" ? setAddQuery(query) : setSubQuery(query);
		if (!query.trim()) {
			target === "add" ? setAddResults([]) : setSubResults([]);
			return;
		}
		// Tenant isolation: NEVER search the index unscoped — always filter by the
		// order's storeId + companyId, or results leak across stores/companies.
		const { hits } = await productsIndex.search<TProduct>(query, {
			filters: `storeId:${order.storeId} AND companyId:${order.companyId}`,
			hitsPerPage: 6,
		});
		target === "add" ? setAddResults(hits) : setSubResults(hits);
	}

	function changeQty(idx: number, delta: number) {
		setItems((prev) =>
			prev.map((it, i) =>
				i === idx ? { ...it, amount: clampQty(it.amount + delta, isWeightItem(it)) } : it,
			),
		);
	}
	function setQty(idx: number, amount: number) {
		setItems((prev) =>
			prev.map((it, i) => (i === idx ? { ...it, amount: clampQty(amount, isWeightItem(it)) } : it)),
		);
	}
	function removeItem(idx: number) {
		setItems((prev) => prev.filter((_, i) => i !== idx));
	}
	function resetAdd() {
		setAddOpen(false);
		setAddQuery("");
		setAddResults([]);
		setAddPending(null);
		setAddQty(1);
	}
	// Picking a product from the search results doesn't add it yet — it moves to
	// the qty step so the admin can set how many/how much before confirming.
	function pickProductToAdd(product: TProduct) {
		setAddPending(product);
		setAddQty(1);
		setAddResults([]);
	}
	function addItem(product: TProduct, amount: number) {
		setItems((prev) => {
			// Product already on a plain line at the same price → bump its quantity
			// instead of adding a second line for the same product.
			const existing = prev.findIndex(
				(it) =>
					it.product.id === product.id &&
					(!it.status || it.status === "delivered") &&
					unit(it) === product.price,
			);
			if (existing !== -1)
				return prev.map((it, i) =>
					i === existing
						? { ...it, amount: clampQty(it.amount + amount, isWeightProduct(product)) }
						: it,
				);
			return [
				...prev,
				{
					product,
					amount: clampQty(amount, isWeightProduct(product)),
					finalPrice: product.price,
					originalPrice: product.price,
					status: "delivered",
				},
			];
		});
		resetAdd();
	}
	function setStatus(idx: number, status: TFulfillmentStatus) {
		setItems((prev) =>
			prev.map((it, i) =>
				i === idx
					? { ...it, status, substitutedWith: status === "substituted" ? it.substitutedWith : null }
					: it,
			),
		);
		if (status === "substituted") setSubFor(idx);
	}
	function setSubstitution(idx: number, product: TProduct) {
		setItems((prev) =>
			prev.map((it, i) =>
				i === idx
					? {
							...it,
							status: "substituted",
							substitutedWith: { product, amount: it.amount, price: product.price },
						}
					: it,
			),
		);
		setSubFor(null);
		setSubQuery("");
		setSubResults([]);
	}
	function setSubQty(idx: number, amount: number) {
		setItems((prev) =>
			prev.map((it, i) =>
				i === idx && it.substitutedWith
					? {
							...it,
							substitutedWith: {
								...it.substitutedWith,
								amount: clampQty(amount, isWeightProduct(it.substitutedWith.product)),
							},
						}
					: it,
			),
		);
	}

	function fulfilledCost() {
		const effective = items
			.filter((i) => i.status !== "missing")
			.map((i) =>
				i.status === "substituted" && i.substitutedWith
					? {
							...i,
							product: i.substitutedWith.product,
							amount: i.substitutedWith.amount,
							finalPrice: i.substitutedWith.price,
							originalPrice: i.substitutedWith.price,
						}
					: i,
			);
		return getCartCost({
			cart: effective,
			discounts,
			deliveryPrice:
				store?.deliveryPrice ?? order.storeOptions?.deliveryPrice ?? order.cart.deliveryPrice ?? 0,
			freeDeliveryPrice: store?.freeDeliveryPrice ?? order.storeOptions?.freeDeliveryPrice ?? 0,
			freeShipping,
			isVatIncludedInPrice:
				store?.isVatIncludedInPrice ?? order.storeOptions?.isVatIncludedInPrice ?? false,
		});
	}

	async function save() {
		setSaving(true);
		try {
			const cost = fulfilledCost();
			const account = accounts.find((a) => a.id === accountId);
			const updated: TOrder = {
				...order,
				clientComment: notes,
				...(account ? { billingAccount: account } : {}),
				storeOptions: { ...order.storeOptions, freeShipping },
				cart: {
					...order.cart,
					items,
					cartTotal: cost.finalCost,
					cartVat: cost.vat,
					cartDiscount: cost.discount,
					deliveryPrice: cost.deliveryPrice,
				},
			};
			const res = await appApi.admin.saveOrder({ order: updated });
			if (res?.success) {
				onSaved?.(updated);
				close();
			}
		} finally {
			setSaving(false);
		}
	}

	const cost = fulfilledCost();

	return (
		<Modal.Backdrop
			isOpen
			onOpenChange={(open) => {
				if (!open) close();
			}}
		>
			<Modal.Container size="lg" scroll="inside" placement="center">
				<Modal.Dialog className="max-h-[85vh] flex flex-col">
					<Modal.Header>
						<Modal.Heading>
							<div className="text-start">
								{t("ordersPage:edit.title", "עריכת הזמנה")} #{order.id.slice(-8)}
							</div>
						</Modal.Heading>
					</Modal.Header>
					<Modal.Body className="space-y-4 flex-1 min-h-0 overflow-y-auto">
						{/* Billing account + notes */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{accounts.length > 0 && (
								<div className="flex flex-col gap-1">
									<label className="text-sm font-medium text-gray-700 text-start">
										{t("ordersPage:edit.billingAccount", "חשבון לחיוב")}
									</label>
									<select
										value={accountId}
										onChange={(e) => setAccountId(e.target.value)}
										className="rounded-md border border-gray-200 px-3 py-2 text-sm"
									>
										<option value="">—</option>
										{accounts.map((a) => (
											<option key={a.id} value={a.id}>
												{a.name} ({a.number})
											</option>
										))}
									</select>
								</div>
							)}
							<div className="flex flex-col gap-1 sm:col-span-2">
								<label className="text-sm font-medium text-gray-700 text-start">
									{t("ordersPage:edit.notes", "הערות")}
								</label>
								<Input value={notes} onChange={(e) => setNotes(e.target.value)} />
							</div>
						</div>

						{/* Items */}
						<div className="flex items-center justify-between border-b pb-2">
							<span className="text-sm font-semibold">
								{t("ordersPage:edit.items", "פריטים בהזמנה")}
							</span>
							<Button variant="ghost" onPress={() => (addOpen ? resetAdd() : setAddOpen(true))}>
								{t("ordersPage:edit.addItem", "+ הוסף פריט")}
							</Button>
						</div>

						{addOpen && (
							<div className="rounded-md border border-gray-200 p-2">
								{!addPending && (
									<>
										<Input
											placeholder={t("ordersPage:edit.searchProduct", "חפש מוצר להוספה…")}
											value={addQuery}
											onChange={(e) => search(e.target.value, "add")}
											autoFocus
										/>
										{addResults.length > 0 && (
											<div className="mt-1 max-h-48 overflow-auto rounded-md border border-gray-100">
												{addResults.map((p) => (
													<button
														key={p.id}
														type="button"
														onClick={() => pickProductToAdd(p)}
														className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 text-start"
													>
														<span>{p.name?.[0]?.value}</span>
														<span className="text-gray-500">{formatter.price(p.price)}</span>
													</button>
												))}
											</div>
										)}
									</>
								)}

								{/* qty step — set the amount before the item joins the order */}
								{addPending && (
									<div className="flex items-center justify-between gap-3 flex-wrap">
										<div className="min-w-[35%]">
											<div className="font-medium">{addPending.name?.[0]?.value}</div>
											<div className="text-xs text-gray-500">
												{formatter.price(addPending.price)} · {addPending.brand || ""}
											</div>
										</div>
										<div className="flex items-center gap-2 flex-wrap">
											<label className="flex items-center gap-1 text-sm">
												{t("ordersPage:edit.qty", "כמות")}:
												<div className="inline-flex items-center rounded-full bg-gray-100 p-0.5">
													<button
														type="button"
														onClick={() =>
															setAddQty((q) =>
																clampQty(
																	q - (isWeightProduct(addPending) ? WEIGHT_STEP : 1),
																	isWeightProduct(addPending),
																),
															)
														}
														className="h-6 w-6 rounded-full bg-white font-bold"
													>
														−
													</button>
													<QtyInput
														value={addQty}
														weight={isWeightProduct(addPending)}
														onCommit={(n) => setAddQty(n)}
													/>
													<button
														type="button"
														onClick={() =>
															setAddQty((q) =>
																clampQty(
																	q + (isWeightProduct(addPending) ? WEIGHT_STEP : 1),
																	isWeightProduct(addPending),
																),
															)
														}
														className="h-6 w-6 rounded-full bg-white font-bold"
													>
														+
													</button>
												</div>
											</label>
											<span className="min-w-20 text-end text-sm font-semibold">
												{formatter.price(addPending.price * addQty)}
											</span>
											<Button variant="primary" onPress={() => addItem(addPending, addQty)}>
												{t("ordersPage:edit.confirmAdd", "הוסף להזמנה")}
											</Button>
											<button
												type="button"
												className="text-xs text-gray-500 underline"
												onClick={() => setAddPending(null)}
											>
												{t("ordersPage:edit.changeProduct", "בחר מוצר אחר")}
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						<div className="space-y-2">
							{items.map((it, idx) => {
								const struck = it.status === "missing" || it.status === "substituted";
								return (
									<div key={`${it.product.id}-${idx}`} className="rounded-lg border border-gray-200 p-3">
										<div className="flex items-center justify-between gap-3 flex-wrap">
											<div className="min-w-[40%]">
												<div className={`font-medium ${struck ? "line-through text-gray-400" : ""}`}>
													{nameOf(it)}
												</div>
												<div className="text-xs text-gray-500">
													{formatter.price(unit(it))} · {it.product.brand || ""}
													{it.status === "substituted" && it.substitutedWith
														? ` · 🔄 ${it.substitutedWith.product.name?.[0]?.value} × ${it.substitutedWith.amount}`
														: ""}
												</div>
											</div>
											<div className="flex items-center gap-2 flex-wrap">
												{/* qty stepper — weight products step by 0.5 and accept a typed
												    decimal weight (e.g. 4.8); unit products step by 1 */}
												<div className="inline-flex items-center rounded-full bg-gray-100 p-0.5">
													<button
														type="button"
														onClick={() => changeQty(idx, isWeightItem(it) ? -WEIGHT_STEP : -1)}
														className="h-6 w-6 rounded-full bg-white font-bold"
													>
														−
													</button>
													<QtyInput
														value={it.amount}
														weight={isWeightItem(it)}
														onCommit={(n) => setQty(idx, n)}
													/>
													<button
														type="button"
														onClick={() => changeQty(idx, isWeightItem(it) ? WEIGHT_STEP : 1)}
														className="h-6 w-6 rounded-full bg-white font-bold"
													>
														+
													</button>
												</div>
												{/* status dropdown */}
												<select
													value={it.status ?? "delivered"}
													onChange={(e) => setStatus(idx, e.target.value as TFulfillmentStatus)}
													className="rounded-md border border-gray-200 px-2 py-1.5 text-xs"
												>
													<option value="delivered">✅ זמין</option>
													<option value="missing">❌ חסר — הסר</option>
													<option value="substituted">🔄 חסר — החלף</option>
												</select>
												<span className="min-w-20 text-end text-sm font-semibold">
													{formatter.price(lineTotal(it))}
												</span>
												<button
													type="button"
													onClick={() => removeItem(idx)}
													className="text-gray-400 hover:text-red-600"
													title={t("ordersPage:edit.remove", "הסר")}
												>
													🗑
												</button>
											</div>
										</div>

										{/* substitution picker */}
										{it.status === "substituted" && (
											<div className="mt-2 rounded-md bg-[#f3f8f4] p-2">
												{it.substitutedWith && (
													<div className="flex items-center justify-between gap-2 flex-wrap text-sm">
														<span className="text-[#155f30]">
															✓ {it.substitutedWith.product.name?.[0]?.value}
														</span>
														<label className="flex items-center gap-1">
															{t("ordersPage:edit.subQty", "כמות")}:
															<QtyInput
																value={it.substitutedWith.amount}
																weight={isWeightProduct(it.substitutedWith.product)}
																onCommit={(n) => setSubQty(idx, n)}
																className="w-16 rounded-md border border-gray-200 px-2 py-1 text-sm text-center outline-none"
															/>
														</label>
														<button
															type="button"
															className="text-xs text-gray-500 underline"
															onClick={() => setSubFor(idx)}
														>
															{t("ordersPage:edit.changeSub", "שנה")}
														</button>
													</div>
												)}
												{subFor === idx && (
													<div className="mt-2">
														<Input
															placeholder={t("ordersPage:edit.searchSub", "חפש מוצר תחליף…")}
															value={subQuery}
													onChange={(e) => search(e.target.value, idx)}
															autoFocus
														/>
														{subResults.length > 0 && (
															<div className="mt-1 max-h-40 overflow-auto rounded-md border border-gray-100 bg-white">
																{subResults.map((p) => (
																	<button
																		key={p.id}
																		type="button"
																		onClick={() => setSubstitution(idx, p)}
																		className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 text-start"
																	>
																		<span>{p.name?.[0]?.value}</span>
																		<span className="text-gray-500">{formatter.price(p.price)}</span>
																	</button>
																))}
															</div>
														)}
													</div>
												)}
											</div>
										)}
									</div>
								);
							})}
						</div>

						<div className="flex items-center justify-between border-t pt-3">
							<label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
								<input
									type="checkbox"
									checked={freeShipping}
									onChange={(e) => setFreeShipping(e.target.checked)}
									className="size-4 accent-[#155f30]"
								/>
								{t("ordersPage:edit.waiveDelivery", "ללא דמי משלוח")}
							</label>
							{!freeShipping && cost.deliveryPrice > 0 && (
								<span className="text-sm text-gray-500">
									{t("ordersPage:edit.delivery", "דמי משלוח")}: {formatter.price(cost.deliveryPrice)}
								</span>
							)}
						</div>

						<div className="flex items-center justify-between pt-1 font-bold">
							<span>{t("ordersPage:edit.total", "סה״כ הזמנה")}</span>
							<span>{formatter.price(cost.finalCost)}</span>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button variant="ghost" onPress={close}>
							{t("common:cancel", "ביטול")}
						</Button>
						<Button
							variant="primary"
							isPending={saving}
							isDisabled={items.length === 0}
							onPress={save}
						>
							{t("ordersPage:edit.save", "שמור שינויים")}
						</Button>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
